import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { padColumns } from 'utils/pad-columns';

import {
  type GetDisbursementPayoutRequestsQueryError,
  type GetDisbursementPayoutRequestsQueryResult,
  useGetDisbursementPayoutRequests,
  type PayoutRequestItem,
  type PostDisbursementPayoutReportsBodyStatusCategoriesItem as StatusCategoriesItem
} from 'api';
import { Restricted } from 'containers/Restricted';

import { initialState } from 'reduxLocal/slices/pages.payoutsSlice';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';
import { useHandleRowClick } from 'hooks/useHandleRowClick';
import { useVirtualList } from 'hooks/useVirtualList';

import { DataTable } from 'components/ui/organisms/DataTable';

import { getColumns } from './columns';
import { PAYOUT_REQUEST_TABLE_TEST_ID } from './constants';
import { CreatePayoutsExportButton } from './CreatePayoutsExportButton';
import { PayoutDetails } from './Details/PayoutDetails';
import { useLocalState, stateToRequestParams } from './localState';

export const PayoutsTableInner = ({ batchId }: { batchId?: string }) => {
  const {
    state: localState,
    updateCursorAfter,
    updateFilters
  } = useLocalState();

  const { filters: globalFilters } = useGlobalFilters();
  const organisationId = useOrganisationId<number>();
  const { findAccountByAccountNumber } = useVirtualAccounts();

  const {
    data: payouts,
    isFetching,
    refetch
  } = useGetDisbursementPayoutRequests<
    GetDisbursementPayoutRequestsQueryResult,
    GetDisbursementPayoutRequestsQueryError
  >(
    organisationId,
    stateToRequestParams({
      ...localState,
      ...globalFilters,
      batchId
    })
  );

  const [searchParams] = useSearchParams();

  useEffect(() => {
    updateFilters({
      ...initialState.filters,
      state: (searchParams.get('state') ?? initialState.filters.state) as
        | StatusCategoriesItem
        | 'All'
        | undefined,
      reasonCode:
        searchParams.get('reasonCode') ?? initialState.filters.reasonCode
    });
  }, [searchParams, updateFilters]);

  const payoutRequestColumns = useMemo(
    () => padColumns(getColumns(findAccountByAccountNumber)),
    [findAccountByAccountNumber]
  );

  const { onScroll, data } = useVirtualList<PayoutRequestItem>({
    data: payouts?.data,
    uniqueKey: 'id',
    isFetching,
    initialRefValue: payouts?.meta?.cursor_next,
    updateCursorCallback: updateCursorAfter,
    resetConditions: [globalFilters, localState.filters, updateCursorAfter],
    cursorNext: payouts?.meta?.has_next_page
      ? payouts?.meta?.cursor_next
      : undefined
  });

  const [selectedRowData, setSelectedRowData] = useState<PayoutRequestItem>();
  const handleRowClick =
    useHandleRowClick<PayoutRequestItem>(setSelectedRowData);

  return (
    <DataTable
      total={payouts?.meta?.total}
      tableActions={
        <Restricted requiredPermission="exports:manage">
          <CreatePayoutsExportButton />
        </Restricted>
      }
      dataSource={data}
      columns={payoutRequestColumns}
      onScroll={onScroll}
      onRowClick={handleRowClick}
      isLoading={isFetching}
      rowKey="id"
      selectedRow={selectedRowData}
      tableDataTestId={PAYOUT_REQUEST_TABLE_TEST_ID}
      rowDataTestId="payouts-table-row-entry"
      rightPanel={
        selectedRowData?.unique_reference ? (
          <PayoutDetails
            onClose={() => setSelectedRowData(undefined)}
            onTestCaseSuccess={refetch}
            uniqueReferenceId={selectedRowData.unique_reference}
          />
        ) : undefined
      }
    />
  );
};

export const PayoutsTable = ({ batchId }: { batchId?: string }) => {
  const { state: localState } = useLocalState();

  return <PayoutsTableInner key={localState.key} batchId={batchId} />;
};
